import { useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Sparkles, Loader2, Check, X, Wand2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyProduct = () => ({ crop_name: "", product_name: "", quantity: "", quantity_unit: "", target_disease: "" });

export default function AIOtherCropsInput({ onCreated }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [saving, setSaving] = useState(false);
  const [edit, setEdit] = useState({});

  const reset = () => {
    setText("");
    setParsed(null);
    setEdit({});
    setOpen(false);
  };

  const onParse = async () => {
    if (!text.trim()) { toast.error(t("aiOtherCrops.emptyTextError")); return; }
    setParsing(true);
    setParsed(null);
    try {
      const r = await axios.post(`${API}/other-crops/parse`, { text, language: i18n.language }, { withCredentials: true, timeout: 60000 });
      const data = r.data;
      if (data.kind === "unknown" || !data.products || data.products.length === 0) {
        toast.warning(t("aiOtherCrops.unclearError"));
        return;
      }
      setParsed(data);
      setEdit({
        treatment_date: data.treatment_date || new Date().toISOString().slice(0, 10),
        zone: data.zone || "",
        surface_treated: data.surface_treated != null ? String(data.surface_treated) : "",
        operator_name: data.operator_name || "",
        notes: data.notes || "",
        products: data.products.map((p) => ({
          crop_name: p.crop_name || "",
          product_name: p.product_name || "",
          quantity: p.quantity != null ? String(p.quantity) : "",
          quantity_unit: p.quantity_unit || "",
          target_disease: p.target_disease || "",
        })),
      });
    } catch (e) {
      toast.error(t("aiOtherCrops.parseError"));
    } finally { setParsing(false); }
  };

  const updateProduct = (idx, field, value) => {
    setEdit((e) => ({ ...e, products: e.products.map((p, i) => (i === idx ? { ...p, [field]: value } : p)) }));
  };

  const addProduct = () => setEdit((e) => ({ ...e, products: [...(e.products || []), emptyProduct()] }));

  const removeProduct = (idx) => {
    setEdit((e) => ({ ...e, products: e.products.length === 1 ? e.products : e.products.filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    if (!parsed) return;
    const products = (edit.products || []).filter((p) => p.crop_name.trim() || p.product_name.trim());
    if (products.length === 0) {
      toast.error(t("aiOtherCrops.addProductError"));
      return;
    }
    const incomplete = products.some((p) => !p.crop_name.trim() || !p.product_name.trim() || !p.quantity || !p.quantity_unit.trim());
    if (incomplete) {
      toast.error(t("aiOtherCrops.incompleteProductError"));
      return;
    }
    setSaving(true);
    try {
      for (const p of products) {
        const payload = {
          crop_name: p.crop_name.trim(),
          product_name: p.product_name.trim(),
          treatment_date: edit.treatment_date,
          quantity: Number(p.quantity),
          quantity_unit: p.quantity_unit.trim(),
          zone: edit.zone || null,
          surface_treated: edit.surface_treated !== "" ? Number(edit.surface_treated) : null,
          target_disease: p.target_disease.trim() || null,
          operator_name: edit.operator_name || null,
          notes: edit.notes || null,
        };
        await axios.post(`${API}/other-crop-treatments`, payload, { withCredentials: true });
      }
      toast.success(t("aiOtherCrops.savedSuccess", { count: products.length }));
      onCreated?.();
      reset();
    } catch (e) {
      toast.error(t("aiOtherCrops.saveError"));
    } finally { setSaving(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        className="vs-btn-primary w-full inline-flex items-center justify-center gap-2 mb-4"
        style={{ background: "var(--gradient-warm)", borderColor: "var(--gold)", color: "white" }}
        data-testid="ai-other-crops-open">
        <Wand2 size={18} /> {t("aiOtherCrops.openButton")}
      </button>
    );
  }

  return (
    <div className="vs-card p-4 mb-4" style={{ borderColor: "var(--gold)", borderWidth: 2 }} data-testid="ai-other-crops">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles size={20} style={{ color: "var(--gold)" }} />
          <h3 className="heading font-bold">{t("aiOtherCrops.title")}</h3>
        </div>
        <button onClick={reset} className="p-1"><X size={18} /></button>
      </div>

      {!parsed ? (
        <>
          <p className="text-sm mb-2" style={{ color: "var(--text-secondary)" }}>
            {t("aiOtherCrops.instruction")}
          </p>
          <textarea
            className="vs-input mb-2"
            rows="3"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t("aiOtherCrops.textareaPlaceholder")}
            data-testid="ai-other-crops-text"
          />
          <button onClick={onParse} disabled={parsing}
            className="vs-btn-primary w-full inline-flex items-center justify-center gap-2"
            style={{ background: "var(--gradient-vine)", borderColor: "var(--primary)" }}
            data-testid="ai-other-crops-parse">
            {parsing ? <><Loader2 size={18} className="animate-spin" /> {t("aiOtherCrops.parseButtonLoading")}</> : <><Wand2 size={18} /> {t("aiOtherCrops.parseButtonLabel")}</>}
          </button>
        </>
      ) : (
        <>
          <div className="p-2 rounded mb-3 text-xs" style={{ background: "var(--secondary)" }}>
            ✨ {t("aiOtherCrops.recognizedLabel")} <strong>{t("aiOtherCrops.treatmentsRecognized", { count: edit.products?.length || 0 })}</strong>
          </div>

          <div className="space-y-2 mb-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="vs-label">{t("aiOtherCrops.dateLabel")}</div>
                <input type="date" className="vs-input" value={edit.treatment_date}
                  onChange={(e) => setEdit({ ...edit, treatment_date: e.target.value })} />
              </div>
              <div>
                <div className="vs-label">{t("aiOtherCrops.zoneLabel")}</div>
                <input className="vs-input" value={edit.zone}
                  onChange={(e) => setEdit({ ...edit, zone: e.target.value })}
                  placeholder={t("otherCrops.fields.zonePlaceholder")} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="vs-label">{t("otherCrops.fields.surface")}</div>
                <input type="number" className="vs-input" value={edit.surface_treated}
                  onChange={(e) => setEdit({ ...edit, surface_treated: e.target.value })} />
              </div>
              <div>
                <div className="vs-label">{t("aiOtherCrops.operatorLabel")}</div>
                <input className="vs-input" value={edit.operator_name}
                  onChange={(e) => setEdit({ ...edit, operator_name: e.target.value })} />
              </div>
            </div>
          </div>

          <div className="mb-3">
            <div className="flex items-center justify-between mb-2">
              <div className="vs-label" style={{ marginBottom: 0 }}>
                {t("aiOtherCrops.productsCountLabel", { count: edit.products?.length || 0 })}
              </div>
              <button onClick={addProduct}
                className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded font-semibold"
                style={{ background: "var(--primary)", color: "white" }}
                data-testid="ai-other-crops-add-product">
                <Plus size={14} /> {t("aiOtherCrops.addButton")}
              </button>
            </div>
            <div className="space-y-3">
              {(edit.products || []).map((p, idx) => (
                <div key={idx} className="p-3 rounded-lg"
                  style={{ background: "var(--secondary)", border: "1px solid var(--border)" }}
                  data-testid={`ai-other-crops-product-${idx}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold" style={{ color: "var(--text-muted)" }}>
                      {t("aiOtherCrops.productNumberLabel", { num: idx + 1 })}
                    </span>
                    {edit.products.length > 1 && (
                      <button onClick={() => removeProduct(idx)} className="p-1"
                        data-testid={`ai-other-crops-remove-product-${idx}`}>
                        <Trash2 size={14} style={{ color: "#c00" }} />
                      </button>
                    )}
                  </div>
                  <div className="space-y-2">
                    <div>
                      <div className="vs-label">{t("otherCrops.fields.crop")}</div>
                      <input className="vs-input" value={p.crop_name}
                        onChange={(e) => updateProduct(idx, "crop_name", e.target.value)}
                        placeholder={t("otherCrops.fields.cropPlaceholder")} />
                    </div>
                    <div>
                      <div className="vs-label">{t("otherCrops.fields.product")}</div>
                      <input className="vs-input" value={p.product_name}
                        onChange={(e) => updateProduct(idx, "product_name", e.target.value)} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <div className="vs-label">{t("otherCrops.fields.quantity")}</div>
                        <input type="number" className="vs-input" value={p.quantity}
                          onChange={(e) => updateProduct(idx, "quantity", e.target.value)} />
                      </div>
                      <div>
                        <div className="vs-label">{t("otherCrops.fields.quantityUnit")}</div>
                        <input className="vs-input" value={p.quantity_unit}
                          onChange={(e) => updateProduct(idx, "quantity_unit", e.target.value)}
                          placeholder="kg, L..." />
                      </div>
                    </div>
                    <div>
                      <div className="vs-label">{t("otherCrops.fields.disease")}</div>
                      <input className="vs-input" value={p.target_disease}
                        onChange={(e) => updateProduct(idx, "target_disease", e.target.value)} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            <button onClick={() => setParsed(null)} className="vs-btn-secondary flex-1" disabled={saving}>
              {t("aiOtherCrops.rewriteButton")}
            </button>
            <button onClick={save} disabled={saving}
              className="vs-btn-primary flex-1 inline-flex items-center justify-center gap-2"
              style={{ background: "var(--gradient-vine)", borderColor: "var(--primary)" }}
              data-testid="ai-other-crops-save">
              <Check size={16} /> {saving ? t("aiOtherCrops.saving") : t("aiOtherCrops.saveButton")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
